import { useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useNavigate, useParams } from '@tanstack/react-router'

import { ArrowDownTrayIcon } from '@gto/icons-react/outline/arrow-down-tray'
import { ArrowLeftIcon } from '@gto/icons-react/outline/arrow-left'
import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { Skeleton } from '@gto/ui/skeleton'
import { Tooltip } from '@gto/ui/tooltip'
import { Typography } from '@gto/ui/typography'

import { ConfirmDialog } from '@/components/confirm-dialog'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { DataTable } from '@/components/data-table'
import { InlineQueryError } from '@/components/inline-query-error'
import { MutationError } from '@/components/mutation-error'
import { Page } from '@/components/page'
import { StatsCard } from '@/components/stats-card'
import { StatusBadge } from '@/components/status-badge'
import { fluidColumn } from '@/components/table-columns'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { downloadBlob } from '@/utils/download-blob'
import {
  extractBlobValidationDetail,
  extractValidationDetail,
} from '@/utils/extract-validation-detail'

import {
  type SettlementPayout,
  useCancelSettlement,
  useConfirmSettlement,
  useExportSettlementCsv,
  useMarkSettlementPaid,
  useProcessSettlement,
  useSettlementDetail,
} from './api'
import {
  canExportSettlementCsv,
  isAutoProcessProvider,
  isKnownSettlementProvider,
  isManualCompletionProvider,
  PROVIDER_LABELS,
} from './labels'

type ConfirmAction = 'confirm' | 'process' | 'markPaid' | 'cancel'

export const SettlementDetailPage = () => {
  const { formatMessage, formatNumber } = useIntl()
  const navigate = useNavigate()
  const { settlementId } = useParams({ strict: false })

  const settlementQuery = useSettlementDetail(settlementId ?? '')
  const { data: settlement, isLoading, isError, refetch } = settlementQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  // Reports on TERMINAL failure so retries don't multiply Sentry events.
  useReportQueryError(settlementQuery, 'useSettlementDetail failed: ')

  const confirmMutation = useConfirmSettlement()
  const processMutation = useProcessSettlement()
  const markPaidMutation = useMarkSettlementPaid()
  const cancelMutation = useCancelSettlement()
  const exportMutation = useExportSettlementCsv()

  // Money-critical: a failed confirm/process/mark-paid/cancel must reach Sentry AND
  // surface to the operator at the action site, and must NOT fire the success
  // side-effect (close the dialog / navigate away).
  const confirmError = useMutationErrorHandler('useConfirmSettlement failed: ')
  const processError = useMutationErrorHandler('useProcessSettlement failed: ')
  // The mark-paid 422 (non-READY / incompatible payout method) carries an
  // actionable `detail`; surface it in the inline alert instead of the generic
  // fallback. Observability is unchanged — the raw error still reaches Sentry.
  const markPaidError = useMutationErrorHandler('useMarkSettlementPaid failed: ', {
    extractDetail: extractValidationDetail,
  })
  const cancelError = useMutationErrorHandler('useCancelSettlement failed: ')
  const exportError = useMutationErrorHandler('useExportSettlementCsv failed: ')

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  // The export 422's partner-names detail lives in the (blob) error body and is
  // read asynchronously, so it can't flow through the sync mutation-error hook.
  const [exportDetail, setExportDetail] = useState<string | null>(null)

  const openAction = (action: ConfirmAction) => {
    confirmError.clearError()
    processError.clearError()
    markPaidError.clearError()
    cancelError.clearError()
    setConfirmAction(action)
  }

  if (isError) {
    return (
      <Page>
        <Page.Header title={<FormattedMessage id="admin.settlements.detail" />} />
        <Page.Content>
          <InlineQueryError
            data-tst="settlement-detail-error"
            messageId="admin.settlements.detailError"
            onRetry={() => void refetch()}
          />
        </Page.Content>
      </Page>
    )
  }

  if (isLoading || settlement == null) {
    return (
      <Page>
        <Page.Header
          title={
            <Skeleton
              height={40}
              width={200}
            />
          }
        />
        <Page.Content>
          <Skeleton height={100} />
          <Skeleton height={300} />
        </Page.Content>
      </Page>
    )
  }

  const isDraft = settlement.status === 'DRAFT'
  const isReady = settlement.status === 'READY'
  const isProcessing = settlement.status === 'PROCESSING'
  const anyLoading =
    confirmMutation.isPending ||
    processMutation.isPending ||
    markPaidMutation.isPending ||
    cancelMutation.isPending
  // PayPal auto-processes ("Pay Settlement"); every non-PayPal provider is closed
  // by hand ("Mark as Paid"). CSV export is narrower — only Wise/Confirmo have a
  // file to download — so it is gated separately from mark-paid.
  const isAutoProvider = isAutoProcessProvider(settlement.provider)
  const canMarkPaid = isManualCompletionProvider(settlement.provider)
  const canExportCsv = canExportSettlementCsv(settlement.provider)
  // Both confirmations quote the disbursement total the operator is about to
  // release. Grouped, not `toFixed(2)` — "12500.75" is one glance away from
  // "1250.075" on an irreversible action — and in the settlement's OWN currency,
  // because a settlement is not always USD and a wrong symbol misstates the sum.
  const confirmAmount = formatNumber(settlement.total_amount, {
    style: 'currency',
    currency: settlement.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  const handleDownloadCsv = () => {
    exportError.clearError()
    setExportDetail(null)
    exportMutation.mutate(
      { settlementId: settlementId ?? '' },
      {
        onSuccess: (blob) =>
          downloadBlob(
            blob,
            `settlement-SET-${String(settlement.settlement_number).padStart(4, '0')}.csv`,
          ),
        // Observability + the generic alert fire immediately; then upgrade to
        // the 422's partner-names detail once the blob body is parsed. The
        // `.catch` is belt-and-suspenders — the raw error already reached Sentry
        // via `onError`.
        onError: (error) => {
          exportError.onError(error)
          void extractBlobValidationDetail(error)
            .then(setExportDetail)
            .catch(() => setExportDetail(null))
        },
      },
    )
  }

  const handleConfirm = () => {
    confirmMutation.mutate(settlementId ?? '', {
      onSuccess: () => setConfirmAction(null),
      onError: confirmError.onError,
    })
  }

  const handleProcess = () => {
    processMutation.mutate(settlementId ?? '', {
      onSuccess: () => setConfirmAction(null),
      onError: processError.onError,
    })
  }

  const handleMarkPaid = () => {
    markPaidMutation.mutate(settlementId ?? '', {
      onSuccess: () => setConfirmAction(null),
      onError: markPaidError.onError,
    })
  }

  const handleCancel = () => {
    cancelMutation.mutate(settlementId ?? '', {
      onSuccess: () => {
        setConfirmAction(null)
        void navigate({ to: '/admin/settlements' })
      },
      onError: cancelError.onError,
    })
  }

  const downloadCsvButton = canExportCsv ? (
    <Button
      disabled={exportMutation.isPending}
      variant="outlined"
      startIcon={
        <ArrowDownTrayIcon
          height={18}
          width={18}
        />
      }
      onClick={handleDownloadCsv}
    >
      <FormattedMessage id="admin.settlements.downloadCsv" />
    </Button>
  ) : (
    // Disabled buttons swallow hover events, so the tooltip wraps a span.
    <Tooltip content={formatMessage({ id: 'admin.settlements.downloadCsv.unavailable' })}>
      <span>
        <Button
          disabled
          variant="outlined"
          startIcon={
            <ArrowDownTrayIcon
              height={18}
              width={18}
            />
          }
        >
          <FormattedMessage id="admin.settlements.downloadCsv" />
        </Button>
      </span>
    </Tooltip>
  )

  const actionButtons = (
    <div className="flex gap-2">
      {downloadCsvButton}
      {(isDraft || isReady) && (
        <Button
          color="error"
          disabled={anyLoading}
          variant="outlined"
          onClick={() => openAction('cancel')}
        >
          <FormattedMessage id="admin.settlements.cancelSettlement" />
        </Button>
      )}
      {isDraft && (
        <Button
          disabled={anyLoading}
          variant="contained"
          onClick={() => openAction('confirm')}
        >
          <FormattedMessage id="admin.settlements.lockAndReady" />
        </Button>
      )}
      {isReady && isAutoProvider && (
        <Button
          color="primary"
          disabled={anyLoading}
          variant="contained"
          onClick={() => openAction('process')}
        >
          <FormattedMessage id="admin.settlements.paySettlement" />
        </Button>
      )}
      {isReady && canMarkPaid && (
        <Button
          color="primary"
          disabled={anyLoading}
          variant="contained"
          onClick={() => openAction('markPaid')}
        >
          <FormattedMessage id="admin.settlements.markPaid" />
        </Button>
      )}
      {isProcessing && (
        <Button
          disabled
          variant="outlined"
        >
          <FormattedMessage id="admin.settlements.processingStatus" />
        </Button>
      )}
    </div>
  )

  return (
    <>
      <Page>
        <Page.Header
          actions={actionButtons}
          subtitle={`${PROVIDER_LABELS[settlement.provider] ?? settlement.provider} · ${settlement.currency}`}
          title={
            <div className="flex items-center gap-3">
              <Button
                aria-label={formatMessage({ id: 'common.back' })}
                size="small"
                variant="text"
                onClick={() => void navigate({ to: '/admin/settlements' })}
              >
                <ArrowLeftIcon
                  height={20}
                  width={20}
                />
              </Button>
              <Typography variant="h4">
                SET-{String(settlement.settlement_number).padStart(4, '0')}
              </Typography>
              <StatusBadge status={settlement.status} />
            </div>
          }
        />

        <Page.Content>
          {/* Every action on this page is gated on a KNOWN provider, so an unrecognised one
              renders a settlement with no way forward and a CSV tooltip that names the wrong
              reason. Say so on screen — the Sentry report reaches us, not the operator. */}
          {!isKnownSettlementProvider(settlement.provider) && (
            <Alert
              data-tst="settlement-unknown-provider"
              severity="warning"
            >
              <FormattedMessage
                id="admin.settlements.unknownProvider"
                values={{ provider: settlement.provider }}
              />
            </Alert>
          )}

          {/* Summary KPIs */}
          <div
            className="flex flex-wrap gap-2"
            data-tst="settlement-summary"
          >
            <DashboardKpiTile
              icon={<CurrencyDollarIcon />}
              label={<FormattedMessage id="admin.settlements.totalAmount" />}
              value={
                <FormattedNumber
                  currency={settlement.currency}
                  style="currency"
                  value={settlement.total_amount}
                />
              }
            />
            <DashboardKpiTile
              icon={<BanknotesIcon />}
              label={<FormattedMessage id="admin.settlements.payoutCount" />}
              value={<FormattedNumber value={settlement.payout_count} />}
            />
            {settlement.created_by_email !== null && (
              <DashboardKpiTile
                label={<FormattedMessage id="admin.settlements.createdBy" />}
                value={<span className="text-base">{settlement.created_by_email}</span>}
              />
            )}
            {settlement.processed_at !== null && (
              <DashboardKpiTile
                label={<FormattedMessage id="admin.settlements.processedAt" />}
                value={
                  <span className="text-base">
                    {new Date(settlement.processed_at).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                }
              />
            )}
          </div>

          {/* Batch ID */}
          {Boolean(settlement.provider_batch_id) && (
            <StatsCard title={<FormattedMessage id="admin.settlements.batchId" />}>
              <Typography variant="body2">{settlement.provider_batch_id}</Typography>
            </StatsCard>
          )}

          {/* Provider error returned with the settlement record */}
          {Boolean(settlement.error_message) && (
            <Alert
              data-tst="settlement-provider-error"
              message={settlement.error_message}
              severity="error"
            />
          )}

          {/* CSV export failure — surfaced inline; the raw error still reaches
              Sentry. A 422 for invalid payout details names the partners in
              `exportDetail`, which supersedes the generic message. */}
          <MutationError
            data-tst="settlement-export-error"
            detail={exportDetail}
            messageId="admin.settlements.exportError"
            show={exportError.hasError}
          />

          {/* Payouts Table */}
          <StatsCard
            icon={<BanknotesIcon />}
            title={<FormattedMessage id="admin.settlements.includedPayouts" />}
          >
            <DataTable
              columns={payoutDetailColumns(formatMessage, settlement.currency)}
              data={settlement.payouts}
              getRowId={(row) => row.id}
            />
          </StatsCard>
        </Page.Content>
      </Page>

      {/* Confirmation dialogs — destructive money actions stay as modals. On
          failure the dialog stays open and shows an inline error at the action
          site rather than silently closing. */}
      <ConfirmDialog
        confirmLabel={<FormattedMessage id="admin.settlements.lockAndReady" />}
        description={<FormattedMessage id="admin.settlements.confirmLockDesc" />}
        loading={confirmMutation.isPending}
        open={confirmAction === 'confirm'}
        title={<FormattedMessage id="admin.settlements.confirmLockTitle" />}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
      >
        <MutationError
          data-tst="settlement-confirm-error"
          messageId="admin.settlements.lockError"
          show={confirmError.hasError}
        />
      </ConfirmDialog>
      <ConfirmDialog
        confirmColor="primary"
        confirmLabel={<FormattedMessage id="admin.settlements.paySettlement" />}
        loading={processMutation.isPending}
        open={confirmAction === 'process'}
        title={<FormattedMessage id="admin.settlements.confirmPayTitle" />}
        description={formatMessage(
          { id: 'admin.settlements.confirmPayBody' },
          {
            amount: confirmAmount,
            count: settlement.payout_count,
            provider: PROVIDER_LABELS[settlement.provider] ?? settlement.provider,
          },
        )}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleProcess}
      >
        <MutationError
          data-tst="settlement-process-error"
          messageId="admin.settlements.processError"
          show={processError.hasError}
        />
      </ConfirmDialog>
      <ConfirmDialog
        confirmColor="primary"
        confirmLabel={<FormattedMessage id="admin.settlements.markPaid" />}
        loading={markPaidMutation.isPending}
        open={confirmAction === 'markPaid'}
        title={<FormattedMessage id="admin.settlements.confirmMarkPaidTitle" />}
        description={formatMessage(
          {
            id: canExportCsv
              ? 'admin.settlements.confirmMarkPaidBody'
              : 'admin.settlements.confirmMarkPaidManualBody',
          },
          {
            amount: confirmAmount,
            count: settlement.payout_count,
            provider: PROVIDER_LABELS[settlement.provider] ?? settlement.provider,
          },
        )}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleMarkPaid}
      >
        <MutationError
          data-tst="settlement-mark-paid-error"
          detail={markPaidError.detail}
          messageId="admin.settlements.markPaidError"
          show={markPaidError.hasError}
        />
      </ConfirmDialog>
      <ConfirmDialog
        confirmColor="error"
        confirmLabel={<FormattedMessage id="admin.settlements.cancelSettlement" />}
        description={<FormattedMessage id="admin.settlements.confirmCancelDesc" />}
        loading={cancelMutation.isPending}
        open={confirmAction === 'cancel'}
        title={<FormattedMessage id="admin.settlements.confirmCancelTitle" />}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleCancel}
      >
        <MutationError
          data-tst="settlement-cancel-error"
          messageId="admin.settlements.cancelError"
          show={cancelError.hasError}
        />
      </ConfirmDialog>
    </>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const payoutDetailColumns = (
  formatMessage: (descriptor: { id: string }) => string,
  currency: string,
): ColumnDef<SettlementPayout>[] =>
  createColumns<SettlementPayout>([
    {
      accessorKey: 'partner_display_name',
      header: formatMessage({ id: 'admin.settlements.partner' }),
      ...fluidColumn({ min: 140, flex: 1.2, stacked: true }),
      cell: ({ getValue, row }) => (
        <div className="flex flex-col">
          {String(getValue())}
          <Typography
            color="text.secondary"
            variant="caption"
          >
            {row.original.partner_email}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: 'requested_amount',
      header: formatMessage({ id: 'admin.settlements.amount' }),
      ...fluidColumn({ min: 90, flex: 0.7, align: 'right' }),
      cell: ({ row }) => (
        <FormattedNumber
          currency={currency}
          style="currency"
          value={row.original.requested_amount}
        />
      ),
    },
    {
      accessorKey: 'provider_transaction_id',
      header: formatMessage({ id: 'admin.settlements.transactionId' }),
      ...fluidColumn({ min: 120, flex: 1 }),
      cell: ({ getValue }) => (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          {getValue() == null ? '—' : String(getValue())}
        </Typography>
      ),
    },
    {
      accessorKey: 'status',
      header: formatMessage({ id: 'admin.settlements.status' }),
      ...fluidColumn({ min: 80, flex: 0.5, align: 'right' }),
      cell: ({ getValue }) => <StatusBadge status={String(getValue())} />,
    },
  ])
